/**
 * Application-level enum constants that mirror PostgreSQL ENUM types.
 * These are used in the frontend for type-safe comparisons and UI rendering.
 * Keep in sync with supabase/migrations/20260217000001_custom_types.sql
 */

// ============================================================
// User Roles
// ============================================================
export const UserRole = {
  ROOT: 'root',
  ADMIN: 'admin',
  SHOP: 'shop',
  USER: 'user',
} as const;

export type UserRole = (typeof UserRole)[keyof typeof UserRole];

export const UserRoleLabels: Record<UserRole, string> = {
  root: 'システム管理者',
  admin: '管理者',
  shop: '店舗管理者',
  user: 'スタッフ',
};

// ============================================================
// Booking Status
// ============================================================
export const BookingStatus = {
  PENDING: 'pending',
  CONFIRMED: 'confirmed',
  IN_PROGRESS: 'in_progress',
  COMPLETED: 'completed',
  CANCELLED: 'cancelled',
  NO_SHOW: 'no_show',
} as const;

export type BookingStatus = (typeof BookingStatus)[keyof typeof BookingStatus];

export const BookingStatusLabels: Record<BookingStatus, string> = {
  pending: '仮予約',
  confirmed: '確定',
  in_progress: '施術中',
  completed: '完了',
  cancelled: 'キャンセル',
  no_show: '無断キャンセル',
};

export const BookingStatusColors: Record<BookingStatus, string> = {
  pending: 'yellow',
  confirmed: 'blue',
  in_progress: 'green',
  completed: 'gray',
  cancelled: 'red',
  no_show: 'orange',
};

// ============================================================
// Booking Kind
// ============================================================
export const BookingKind = {
  ADMIN: 'admin',
  WEB: 'web',
  RECURRING: 'recurring',
  HOTPEPPER: 'hotpepper',
} as const;

export type BookingKind = (typeof BookingKind)[keyof typeof BookingKind];

export const BookingKindLabels: Record<BookingKind, string> = {
  admin: '管理画面',
  web: 'Web予約',
  recurring: '定期予約',
  hotpepper: 'HotPepper',
};

// ============================================================
// Staff Status
// ============================================================
export const StaffStatus = {
  ACTIVE: 'active',
  INACTIVE: 'inactive',
  PENDING_APPROVAL: 'pending_approval',
  LOCKED: 'locked',
} as const;

export type StaffStatus = (typeof StaffStatus)[keyof typeof StaffStatus];

export const StaffStatusLabels: Record<StaffStatus, string> = {
  active: '有効',
  inactive: '無効',
  pending_approval: '承認待ち',
  locked: 'ロック',
};

// ============================================================
// Member Type
// ============================================================
export const MemberType = {
  GENERAL: 'general',
  MEMBER: 'member',
  WITHDRAWN: 'withdrawn',
} as const;

export type MemberType = (typeof MemberType)[keyof typeof MemberType];

export const MemberTypeLabels: Record<MemberType, string> = {
  general: '一般',
  member: '会員',
  withdrawn: '退会',
};

// ============================================================
// Notification Type
// ============================================================
export const NotificationType = {
  CONFIRM: 'confirm',
  REMINDER: 'reminder',
  THANKS: 'thanks',
  HOLDER: 'holder',
  PERSONAL: 'personal',
  TAG: 'tag',
  CHANGE: 'change',
  CANCEL: 'cancel',
  BIRTHDAY: 'birthday',
} as const;

export type NotificationType = (typeof NotificationType)[keyof typeof NotificationType];

export const NotificationTypeLabels: Record<NotificationType, string> = {
  confirm: '予約確認',
  reminder: 'リマインド',
  thanks: 'お礼',
  holder: 'プレースホルダー',
  personal: '個人情報',
  tag: 'タグ',
  change: '予約変更',
  cancel: 'キャンセル',
  birthday: '誕生日',
};

// ============================================================
// Day of Week
// ============================================================
export const DayOfWeek = {
  MON: 'mon',
  TUE: 'tue',
  WED: 'wed',
  THU: 'thu',
  FRI: 'fri',
  SAT: 'sat',
  SUN: 'sun',
  HOLIDAY: 'holiday',
} as const;

export type DayOfWeek = (typeof DayOfWeek)[keyof typeof DayOfWeek];

export const DayOfWeekLabels: Record<DayOfWeek, string> = {
  mon: '月',
  tue: '火',
  wed: '水',
  thu: '木',
  fri: '金',
  sat: '土',
  sun: '日',
  holiday: '祝',
};

// ============================================================
// Log Action
// ============================================================
export const LogAction = {
  CREATE: 'create',
  UPDATE: 'update',
  DELETE: 'delete',
  CANCEL: 'cancel',
  RESTORE: 'restore',
} as const;

export type LogAction = (typeof LogAction)[keyof typeof LogAction];
